import { env } from '@sib/config';

const EXTENSION_TOKEN_CACHE_KEY = 'extension-token-cache';
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

interface CachedTokens {
  idToken: string;
  accessToken: string;
  expiresOn: number;
}

const getSessionStorage = () =>
  env.isExtension ? chrome?.storage?.session : null;

export const cacheExtensionTokens = (tokens: {
  idToken: string;
  accessToken: string;
  expiresOn: Date | null;
}) => {
  const storage = getSessionStorage();
  if (!storage) return;

  const data: CachedTokens = {
    idToken: tokens.idToken,
    accessToken: tokens.accessToken,
    expiresOn: tokens.expiresOn?.getTime() ?? Date.now() + 60 * 60 * 1000,
  };

  storage.set({ [EXTENSION_TOKEN_CACHE_KEY]: JSON.stringify(data) });
};

export const getCachedExtensionTokens = async (): Promise<{
  idToken: string;
  accessToken: string;
} | null> => {
  const storage = getSessionStorage();
  if (!storage) return null;

  return new Promise(resolve => {
    storage.get(EXTENSION_TOKEN_CACHE_KEY, result => {
      try {
        const raw = result[EXTENSION_TOKEN_CACHE_KEY] as string | undefined;
        if (!raw) return resolve(null);

        const data: CachedTokens = JSON.parse(raw);

        if (data.expiresOn - TOKEN_EXPIRY_BUFFER_MS <= Date.now())
          return resolve(null);

        resolve({
          idToken: data.idToken,
          accessToken: data.accessToken,
        });
      } catch {
        resolve(null);
      }
    });
  });
};

export const clearCachedExtensionTokens = () => {
  const storage = getSessionStorage();
  if (!storage) return;

  storage.remove(EXTENSION_TOKEN_CACHE_KEY);
};
